import React, { useEffect } from 'react';
import './PrivacyPolicy.css';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';

const PrivacyPolicy = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    
    // Enable scrolling for this page
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    
    // Cleanup: restore original styles when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, [location]);

  return (
    <div className="privacy-policy">
      <h1>Privacy Policy for Ideal Scribe</h1>
      
      <section>
        <h2>1. Overview</h2>
        <p>
          At Ideal Scribe (referred to as "Ideal Scribe", "we", "our", or "us"), safeguarding your privacy and personal data is fundamental 
          to our mission. This Privacy Policy outlines our practices regarding data collection, usage, storage, and protection when you 
          interact with our platform at https://www.idealscribe.com.
        </p>
        <p>
          We encourage you to read this policy thoroughly to understand how we handle your data. If you have questions or concerns about 
          our privacy practices, please reach out to us at support@idealscribe.com.
        </p>
        <p>
          By accessing or using our Platform, you acknowledge that you have read and understood this Privacy Policy and consent to the 
          data practices described herein. If you do not agree with any aspect of this policy, please discontinue use of our Platform.
        </p>
        <p>
          This Privacy Policy applies to all data collected through our website, mobile applications, and any related features, tools, or 
          services we provide (collectively, the "Platform").
        </p>
      </section>

      <section>
        <h2>2. Data Collection Practices</h2>
        <p>
          We gather data through various means as you interact with our Platform. The types of data we collect depend on how you use our 
          Platform and the features you access.
        </p>
        
        <h3>Data You Provide Directly</h3>
        <p>
          We collect information that you voluntarily provide when you:
        </p>
        <ul>
          <li>Register for an account or create a user profile</li>
          <li>Utilize Platform features such as creating notes, organizing journeys, or building Pack-Its</li>
          <li>Send emails to our designated email address for note creation</li>
          <li>Reach out to our support team</li>
          <li>Subscribe to our communications or participate in promotional activities</li>
          <li>Complete surveys or provide us with feedback</li>
        </ul>
        
        <h3>Categories of Personal Data</h3>
        <p>
          The specific personal data we collect varies based on your interactions with our Platform. This may include:
        </p>
        <ul>
          <li><strong>Account Details:</strong> Your name, email address, chosen username, password (stored securely), and any profile 
          information you choose to share</li>
          <li><strong>Financial Information:</strong> Payment card details, billing addresses, and transaction history (handled securely 
          through certified payment processors)</li>
          <li><strong>User-Generated Content:</strong> Notes, travel plans, Pack-Its, geographic locations, tags, labels, and other 
          content you create or upload</li>
          <li><strong>Geographic Information:</strong> Location data connected to your notes and travel planning activities</li>
          <li><strong>Correspondence:</strong> Email communications, support tickets, and any feedback you provide</li>
        </ul>

        <h3>Automatically Gathered Data</h3>
        <p>
          When you use our Platform, we automatically collect certain technical and usage information. This includes:
        </p>
        <ul>
          <li><strong>Device Details:</strong> Internet Protocol (IP) address, browser specifications, device model, operating system 
          version, and unique device identifiers</li>
          <li><strong>Usage Patterns:</strong> Pages you visit, features you access, duration of sessions, navigation paths, and timestamps 
          of your activities</li>
          <li><strong>Geographic Indicators:</strong> Approximate location derived from IP address or device location services (when enabled)</li>
          <li><strong>Tracking Technologies:</strong> Data collected via cookies, pixel tags, local storage, and similar mechanisms</li>
        </ul>
      </section>

      <section>
        <h2>3. Data Usage and Processing</h2>
        <p>
          We utilize the data we collect for the following purposes:
        </p>
        <ul>
          <li><strong>Platform Operation:</strong> To establish and maintain your account, process payments, and deliver the features and 
          capabilities you request</li>
          <li><strong>Service Enhancement:</strong> To evaluate usage trends, identify areas for improvement, and refine the user experience</li>
          <li><strong>Intelligent Features:</strong> To enable AI-driven capabilities including content organization, personalized 
          recommendations, and automated travel planning tools</li>
          <li><strong>User Communication:</strong> To send important service notifications, respond to your questions, and provide 
          customer assistance</li>
          <li><strong>Marketing Activities:</strong> To deliver promotional content, newsletters, and marketing materials (only with your 
          explicit consent)</li>
          <li><strong>Security Measures:</strong> To identify and address technical problems, detect fraudulent activity, and protect 
          against security threats</li>
          <li><strong>Legal Compliance:</strong> To fulfill our obligations under applicable laws, regulations, and legal proceedings</li>
        </ul>
      </section>

      <section>
        <h2>4. Data Sharing and Disclosure</h2>
        <p>
          We may share your data in the following circumstances:
        </p>
        <ul>
          <li><strong>Business Partners:</strong> We may share data with trusted third-party partners who assist us in operating the Platform, 
          such as payment processors, data analytics providers, cloud infrastructure services, email delivery services, and customer support 
          platforms. These partners are contractually required to maintain confidentiality and use your data solely for the purposes we specify.</li>
          <li><strong>Corporate Transactions:</strong> In the event of a merger, acquisition, sale of assets, or other business transfer, your 
          data may be transferred as part of the transaction.</li>
          <li><strong>With Your Authorization:</strong> We may share your personal data for any purpose if you provide explicit consent.</li>
          <li><strong>Legal Requirements:</strong> We may disclose data when legally compelled to do so, such as in response to court orders, 
          subpoenas, government investigations, or other legal processes.</li>
          <li><strong>Protection of Rights:</strong> We may share data when we believe disclosure is necessary to investigate potential Terms 
          violations, suspected fraud, threats to personal safety, or illegal activities.</li>
        </ul>
        <p>
          We do not engage in the sale of your personal data to third parties for their marketing purposes.
        </p>
      </section>

      <section>
        <h2>5. Integrated Third-Party Platforms</h2>
        <p>
          Our Platform incorporates services provided by third-party companies that may collect data about you. Key integrations include:
        </p>
        <ul>
          <li><strong>Google Maps Platform:</strong> We utilize Google Maps and Places API to deliver mapping and location-based features. 
          Google's data handling practices are governed by their Privacy Policy: https://policies.google.com/privacy</li>
          <li><strong>Amazon Web Services:</strong> We use AWS Amplify and Cognito for user authentication and backend infrastructure. AWS 
          privacy practices are detailed in their Privacy Policy: https://aws.amazon.com/privacy/</li>
          <li><strong>Stripe Payment Services:</strong> We partner with Stripe to process payment transactions. Stripe's data practices are 
          outlined in their Privacy Policy: https://stripe.com/privacy</li>
          <li><strong>Google Analytics Platform:</strong> We employ Google Analytics to understand Platform usage patterns. Google Analytics 
          data handling is governed by Google's Privacy Policy: https://policies.google.com/privacy</li>
        </ul>
        <p>
          We recommend reviewing the privacy policies of these third-party services to understand their data collection and usage practices.
        </p>
      </section>

      <section>
        <h2>6. Information Security</h2>
        <p>
          We employ comprehensive security measures across administrative, technical, and physical dimensions to safeguard your personal data. 
          While we implement industry-standard security practices, please understand that no method of electronic storage or transmission is 
          completely secure, and we cannot guarantee absolute security.
        </p>
        <p>
          Our security measures include:
        </p>
        <ul>
          <li>Data encryption for information both in transit and at rest</li>
          <li>Robust authentication systems and access management controls</li>
          <li>Ongoing security evaluations and timely system updates</li>
          <li>Restricted access to personal data limited to authorized personnel with legitimate business needs</li>
        </ul>
        <p>
          In the event of a security incident affecting your data, we will notify you and relevant authorities as required by applicable law.
        </p>
      </section>

      <section>
        <h2>7. Data Storage Duration</h2>
        <p>
          We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a 
          longer retention period is mandated by law (such as tax regulations, accounting requirements, or other legal obligations).
        </p>
        <p>
          When we no longer have a legitimate business need to process your personal data, we will either delete or anonymize it. If deletion 
          is not immediately possible (for instance, due to backup systems), we will securely store the data and isolate it from further 
          processing until deletion can be completed.
        </p>
      </section>

      <section>
        <h2>8. Your Data Rights</h2>
        <p>
          Depending on your geographic location, you may have various rights concerning your personal data, including:
        </p>
        <ul>
          <li><strong>Right to Access:</strong> You can request a copy of the personal data we hold about you</li>
          <li><strong>Right to Rectification:</strong> You can request correction of any inaccurate or incomplete data</li>
          <li><strong>Right to Erasure:</strong> You can request deletion of your personal data under certain circumstances</li>
          <li><strong>Right to Data Portability:</strong> You can request transfer of your data to another service provider</li>
          <li><strong>Right to Object:</strong> You can object to certain types of data processing</li>
          <li><strong>Right to Restrict Processing:</strong> You can request limitation of how we process your data</li>
          <li><strong>Right to Withdraw Consent:</strong> You can withdraw consent for data processing where consent is the legal basis</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at support@idealscribe.com. We will respond to your request promptly and in 
          accordance with applicable law.
        </p>
        <p>
          Please note that we may need to retain certain data when we have a legal obligation or legitimate interest to do so.
        </p>
      </section>

      <section>
        <h2>9. Protection of Minors</h2>
        <p>
          Our Platform is designed for users who are 18 years of age or older. We do not intentionally collect personal data from individuals 
          under 18 years of age. If you believe a minor has provided us with personal data, please contact us immediately at 
          support@idealscribe.com. Upon discovering that we have collected data from a minor without proper parental consent, we will take 
          immediate steps to remove that information from our systems.
        </p>
      </section>

      <section>
        <h2>10. Email-Based Note Creation</h2>
        <p>
          When you send emails to scribe@quicknotes.idealscribe.com to create notes, we collect and process the following:
        </p>
        <ul>
          <li>The email message content (subject line and body text)</li>
          <li>Your email address</li>
          <li>Email metadata (such as timestamps and headers)</li>
        </ul>
        <p>
          This data is used exclusively to create notes within your account and is protected by the same privacy safeguards as other data 
          you provide through our Platform.
        </p>
      </section>

      <section>
        <h2>11. Cookies and Similar Technologies</h2>
        <p>
          We utilize cookies and related tracking technologies to monitor Platform activity and store certain preferences. Cookies are small 
          text files containing data that are placed on your device when you visit our Platform.
        </p>
        <p>
          We use both temporary cookies (which expire when you close your browser) and persistent cookies (which remain on your device until 
          they expire or you delete them).
        </p>
        <p>
          You can configure your browser to reject cookies or alert you when cookies are being set. However, please note that disabling cookies 
          may limit your ability to use certain Platform features.
        </p>
      </section>

      <section>
        <h2>12. Cross-Border Data Transfers</h2>
        <p>
          Your data may be transferred to and stored on servers located in countries other than your own, where data protection regulations 
          may differ from those in your jurisdiction.
        </p>
        <p>
          If you are located outside the United States and choose to provide data to us, please be aware that we transfer data, including 
          personal data, to the United States for processing.
        </p>
        <p>
          By using our Platform and providing your data, you consent to this transfer and processing in the United States.
        </p>
      </section>

      <section>
        <h2>13. California Consumer Rights</h2>
        <p>
          If you are a resident of California, you have specific rights under the California Consumer Privacy Act (CCPA), including:
        </p>
        <ul>
          <li>The right to know what personal data we collect, use, and disclose</li>
          <li>The right to request deletion of your personal data</li>
          <li>The right to opt-out of the sale of personal data (we do not sell personal data)</li>
          <li>The right to non-discrimination for exercising your privacy rights</li>
        </ul>
        <p>
          To exercise these rights, please contact us at support@idealscribe.com.
        </p>
      </section>

      <section>
        <h2>14. European Economic Area Rights</h2>
        <p>
          If you are located in the European Economic Area (EEA), you have data protection rights under the General Data Protection Regulation 
          (GDPR), as detailed in Section 8 above.
        </p>
        <p>
          Our legal grounds for processing your personal data include:
        </p>
        <ul>
          <li>Fulfillment of our contractual obligations to you</li>
          <li>Your explicit consent</li>
          <li>Compliance with legal requirements</li>
          <li>Our legitimate interests in operating and enhancing our Platform</li>
        </ul>
      </section>

      <section>
        <h2>15. Policy Updates</h2>
        <p>
          We may periodically update this Privacy Policy to reflect changes in our practices or for other operational, legal, or regulatory 
          reasons. When we make changes, we will post the updated policy on this page and update the revision date.
        </p>
        <p>
          We encourage you to review this Privacy Policy regularly to stay informed about how we protect your data. Changes become effective 
          when posted on this page.
        </p>
      </section>

      <section>
        <h2>16. Contact Information</h2>
        <p>
          For questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
        </p>
        <p>
          Email: <a href="mailto:support@idealscribe.com">support@idealscribe.com</a>
        </p>
        <p>
          Last Updated: {new Date().getFullYear()}
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
